import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import {
  CURRENT_STOCK_QUERY_KEY,
  DISPOSAL_URL,
  STOCK_LOGS_QUERY_KEY
} from './queryConstants'
import { DisposalData, MedicationStockReason } from '../types'
import { useUpdateStockRecord } from './useUpdateStockRecord'

type UseDisposalProps = {
  residentId: string
  residentMedicationId: string
  onSuccess?: () => void
  onError?: (error: any) => void
}

type DisposalRequest = {
  residentId: string
  residentMedicationId: string
  actionTime: string
  quantity: number
  witnessIds: string[]
  notes: string
  disposalMethod: string
  disposedBy?: { id: string }
  mediaAttachments: any[]
  discrepancyReason?: string
}

export const useDisposal = ({
  residentId,
  residentMedicationId,
  onSuccess,
  onError
}: UseDisposalProps) => {
  const queryClient = useQueryClient()

  const { updateStockRecord, isLoading: isUpdating } = useUpdateStockRecord({
    onSuccess,
    onError
  })

  const createMutation = useMutation(
    (data: DisposalData) => {
      let actionedDateISO: string

      // Prefer using the dayjs object directly if available (more reliable)
      if (
        data.actionedDateDayjs &&
        typeof data.actionedDateDayjs.isValid === 'function' &&
        data.actionedDateDayjs.isValid()
      ) {
        actionedDateISO = data.actionedDateDayjs.startOf('day').toISOString()
      } else if (data.actionedDate && data.actionedDate.trim() !== '') {
        const parsedDate = dayjs(data.actionedDate, 'DD-MM-YYYY', true)
        if (parsedDate.isValid()) {
          // Use start of day to ensure we preserve the date correctly
          actionedDateISO = parsedDate.startOf('day').toISOString()
        } else {
          // Fallback to current date/time if parsing fails
          actionedDateISO = dayjs().startOf('day').toISOString()
        }
      } else {
        // Fallback to current date/time if no date provided
        actionedDateISO = dayjs().startOf('day').toISOString()
      }

      const hasMedia = data.media && data.media.length > 0

      if (hasMedia) {
        const formData = new FormData()

        formData.append('residentId', residentId)
        formData.append('residentMedicationId', residentMedicationId)
        formData.append('actionTime', actionedDateISO)
        formData.append('quantity', String(data.disposedStock))
        formData.append('notes', data.description)
        formData.append('disposalMethod', data.disposalMethod)
        if (data.actionedBy) {
          formData.append('disposedBy[id]', data.actionedBy)
        }
        if (data.hasDiscrepancy && data.discrepancyReason) {
          formData.append('discrepancyReason', data.discrepancyReason)
        }

        data.media?.forEach((file: File | undefined) => {
          if (file) {
            formData.append('media', file)
          }
        })

        const url = `${DISPOSAL_URL}?witnessCheckRequired=false`

        return postJSON({
          url,
          contentType: 'multipart/form-data',
          body: formData
        })
      }

      const requestBody: DisposalRequest = {
        residentId,
        residentMedicationId,
        actionTime: actionedDateISO,
        quantity: data.disposedStock,
        witnessIds: [],
        notes: data.description,
        disposalMethod: data.disposalMethod,
        disposedBy: data.actionedBy ? { id: data.actionedBy } : undefined,
        mediaAttachments: [],
        discrepancyReason:
          data.hasDiscrepancy && data.discrepancyReason
            ? data.discrepancyReason
            : undefined
      }

      const url = `${DISPOSAL_URL}?witnessCheckRequired=false`

      return postJSON({
        url,
        json: requestBody
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        queryClient.invalidateQueries([CURRENT_STOCK_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error) => {
        console.error('Error saving disposal:', error)
        onError?.(error)
      }
    }
  )

  const updateDisposal = async ({
    recordId,
    data
  }: {
    recordId: string
    data: DisposalData
  }) => {
    let actionTime: string | undefined

    // Prefer using the dayjs object directly if available (more reliable)
    if (
      data.actionedDateDayjs &&
      typeof data.actionedDateDayjs.isValid === 'function' &&
      data.actionedDateDayjs.isValid()
    ) {
      actionTime = data.actionedDateDayjs.startOf('day').toISOString()
    } else if (data.actionedDate && data.actionedDate.trim() !== '') {
      const parsedDate = dayjs(data.actionedDate, 'DD-MM-YYYY', true)
      if (parsedDate.isValid()) {
        // Use start of day to ensure we preserve the date correctly
        actionTime = parsedDate.startOf('day').toISOString()
      }
    }

    return updateStockRecord({
      recordId,
      type: MedicationStockReason.DISPOSAL,
      data: {
        notes: data.description,
        witnessIds: [],
        disposedBy: data.actionedBy || undefined,
        disposalMethod: data.disposalMethod || undefined,
        actionTime,
        discrepancyReason:
          data.hasDiscrepancy && data.discrepancyReason
            ? data.discrepancyReason
            : undefined,
        media: data.media,
        mediaAttachmentKeysToRemove: data.mediaAttachmentKeysToRemove
      }
    })
  }

  return {
    createDisposal: createMutation.mutateAsync,
    updateDisposal,
    isLoading: createMutation.isLoading || isUpdating
  }
}

export default useDisposal
